import React from "react";
import { Select } from "antd";
import { useTranslation } from "react-i18next";
import type { ApiClientRoleOption } from "./apiClientRoles";

interface ApiClientRoleSelectProps {
  roles: ApiClientRoleOption[];
  /** The current role id; null or "" is "No role". */
  value: string | null;
  /** Called with a role id, or "" for "No role". */
  onChange: (roleId: string) => void;
  disabled?: boolean;
  loading?: boolean;
  testId: string;
}

/**
 * The role an API client acts as, picked from the roles of its organization.
 *
 * Shaped like the user editor's role select (FormInputSelectRole): searchable
 * by the plain role name, and a stored role that is not in the list (deleted,
 * or of another organization) still shows as its id rather than as a blank.
 * Unlike a user, an API client may hold no role, so "No role" is an option; it
 * sends "", which LiftCloud reads as "clear the role".
 */
function ApiClientRoleSelect({
  roles,
  value,
  onChange,
  disabled = false,
  loading = false,
  testId,
}: ApiClientRoleSelectProps) {
  const [t] = useTranslation("global");
  const current = value ?? "";
  const unknownCurrent = current !== "" && !roles.some((role) => role.id === current);

  const options = [
    { value: "", label: t("apiClients.noRole") },
    ...(unknownCurrent ? [{ value: current, label: current }] : []),
    ...roles.map((role) => ({ value: role.id, label: role.label })),
  ];

  return (
    <Select
      data-testid={testId}
      className="generalStyles__width100"
      style={{ minWidth: 200 }}
      showSearch
      optionFilterProp="label"
      value={current}
      options={options}
      disabled={disabled}
      loading={loading}
      onChange={(next: string) => onChange(next)}
    />
  );
}

export default ApiClientRoleSelect;
